// frontend/src/super-admin/billing/SubscriptionsListPage.tsx
import * as React from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../components/ui/select';
import { DataTable } from '../../design-system/components/DataTable';
import { EmptyState } from '../../design-system/components/EmptyState';
import { PageHeader } from '../../design-system/components/PageHeader';
import { useApi } from '../../design-system/useApi';
import { formatDate } from '../../design-system/format';
import { VISUAL, statusPill, statusDot, type StatusTone } from '../../design-system/visual';
import { ListTree } from 'lucide-react';
import { billingApi } from '../../modules/platform-billing/api';
import type { TenantSubscription, SubscriptionStatus } from '../../modules/platform-billing/types';
import { useConfirm } from '../../components/ui/confirm-dialog';

type Filter = 'all' | SubscriptionStatus;

export function SubscriptionsListPage() {
  const confirm = useConfirm();
  const [filter, setFilter] = React.useState<Filter>('all');
  const path = filter === 'all'
    ? '/api/admin/billing/subscriptions'
    : `/api/admin/billing/subscriptions?status=${filter}`;
  const { data, loading, reload } = useApi<{ subscriptions: TenantSubscription[] }>(path, [filter]);

  async function cancel(s: TenantSubscription) {
    const who = s.tenant_name || `tenant ${s.tenant_id.slice(0, 8)}`;
    if (!(await confirm({ title: `Cancel subscription for ${who}?`, description: 'This stops billing.', destructive: true, confirmLabel: 'Cancel subscription' }))) return;
    try {
      await billingApi.cancelSubscription(s.tenant_id);
      toast.success('Subscription cancelled');
      reload();
    } catch (err: any) { toast.error('Cancel failed', { description: err.message }); }
  }

  const rows = data?.subscriptions ?? [];

  // KPI counts (over the loaded set — filter narrows them)
  const total = rows.length;
  const byStatus = (s: SubscriptionStatus) => rows.filter((r) => r.status === s).length;

  return (
    <div>
      <PageHeader
        eyebrow="Platform / Billing"
        title="Subscriptions"
        description="All tenant subscriptions — filter by status, cancel if needed."
        actions={
          <Select value={filter} onValueChange={(v) => setFilter(v as Filter)}>
            <SelectTrigger className="h-10 rounded-full bg-card/80 border-border/60 px-4 text-[12.5px] font-semibold w-auto min-w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="trial">Trial</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="past_due">Past due</SelectItem>
              <SelectItem value="read_only">Read only</SelectItem>
              <SelectItem value="suspended">Suspended</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        }
      />
      <div className="px-6 sm:px-8 lg:px-10 pb-12 space-y-6">
        {filter === 'all' && (
          <section className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Kpi label="Total"     value={total}              tone="neutral" />
            <Kpi label="Trial"     value={byStatus('trial')}     tone="info" />
            <Kpi label="Active"    value={byStatus('active')}    tone="success" />
            <Kpi label="Past due"  value={byStatus('past_due')}  tone="warning" />
            <Kpi label="Cancelled" value={byStatus('cancelled')} tone="muted" />
          </section>
        )}

        <section className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
          <DataTable<TenantSubscription>
            loading={loading}
            rows={rows}
            rowKey={(r) => r.tenant_id}
            columns={[
              { key: 'tenant',   header: 'Tenant',   cell: (r) => r.tenant_name
                ? <span className="text-[13px] font-medium">{r.tenant_name}</span>
                : <span className="font-mono text-[12.5px]">{r.tenant_id.slice(0, 8)}…</span> },
              { key: 'status',   header: 'Status',   cell: (r) => statusBadge(r.status) },
              { key: 'currency', header: 'Currency', cell: (r) => r.billing_currency, className: 'text-muted-foreground' },
              { key: 'trial',    header: 'Trial ends', cell: (r) => r.trial_ends_at ? formatDate(r.trial_ends_at) : '—', className: 'text-muted-foreground' },
              { key: 'period',   header: 'Period end', cell: (r) => formatDate(r.current_period_end) },
              { key: 'grace',    header: 'Grace ends', cell: (r) => r.grace_period_ends_at ? formatDate(r.grace_period_ends_at) : '—', className: 'text-muted-foreground' },
              { key: 'actions',  header: '', cell: (r) => r.status !== 'cancelled'
                ? <Button size="sm" variant="ghost" className="rounded-full h-8 text-muted-foreground hover:text-destructive" onClick={() => cancel(r)}>Cancel</Button>
                : null },
            ]}
            emptyState={<EmptyState icon={<ListTree />} title="No subscriptions" description="Subscriptions are auto-created when tenants are created." />}
          />
        </section>
      </div>
    </div>
  );
}

function Kpi({ label, value, tone = 'neutral' }: { label: string; value: number; tone?: 'neutral' | 'info' | 'success' | 'warning' | 'muted' }) {
  const dotClass =
    tone === 'success' ? 'bg-success' :
    tone === 'info'    ? 'bg-info' :
    tone === 'warning' ? 'bg-warning' :
    tone === 'muted'   ? 'bg-muted-foreground/40' :
                         'bg-foreground/40';
  return (
    <div className={[VISUAL.bento, 'p-4'].join(' ')}>
      <div className="flex items-center justify-between mb-2">
        <p className={VISUAL.eyebrow}>{label}</p>
        <span aria-hidden className={['size-2 rounded-full', dotClass].join(' ')} />
      </div>
      <p className="font-sans font-light text-[1.8rem] leading-none tracking-[-0.03em] tabular-nums text-foreground">{value}</p>
    </div>
  );
}

function statusBadge(s: SubscriptionStatus): React.ReactNode {
  const tone: StatusTone =
    s === 'trial' ? 'info' :
    s === 'active' ? 'success' :
    s === 'past_due' ? 'warning' :
    s === 'read_only' ? 'warning' :
    s === 'suspended' ? 'destructive' :
    'muted';
  return (
    <span className={statusPill(tone)}>
      <span className={statusDot(tone)} />
      {s.replace('_', ' ')}
    </span>
  );
}
